/**
 * `IProjectWorkflowModel.ts` — web port of Unity's
 * `Models/WorkflowModel/IProjectWorkflowModel.cs`, re-based on the pure-YAML
 * document.
 *
 * Single owner of the per-project `workflow.yaml` guided-training file: one
 * structured YAML document per project holding the authored steps and
 * AI-prepared scripts. Per-user runtime state is stored separately in
 * DynamoDB. Cloud blob key
 * `account/{customerId}/projectdata/{projectId}/workflow.yaml`. It also
 * maintains the derived DynamoDB search summary; that row is never a read
 * source for authoring or playback.
 *
 * Web deviations from Unity (documented, deliberate):
 *  - `importAsync` takes the YAML TEXT, not a local file path (browsers have
 *    no fs paths — callers read the picked `File` via `.text()` first).
 *  - No local file cache / `.dirty` marker — the loaded document is
 *    memory-only with a debounced cloud upload and a flush on project unload.
 */

import type { Emitter } from "@core/events/emitter";
import type {
  WorkflowDocument,
  WorkflowRunState,
  WorkflowValidationReport,
} from "./WorkflowDocument";
import type { WorkflowUiState } from "./WorkflowDocument";
import type { WorkflowYamlIssue } from "./WorkflowYaml";

/**
 * Port of Unity's `WorkflowChangeOrigin` enum.
 *
 * `api` is web-only: a YAML patch applied through
 * {@link IProjectWorkflowModel.applyYamlAsync} from the dev console. It
 * deliberately behaves like `builder` downstream (the panel re-renders from
 * the model) — it exists as its own value so listeners can tell a
 * programmatic edit from an agent one without guessing.
 */
export type WorkflowChangeOrigin =
  | "load"
  | "import"
  | "builder"
  | "editor"
  | "api"
  /**
   * `workflow_ui` was rewritten because the learner-facing panels were moved.
   * Its own value rather than `api` or `builder` so listeners can tell a
   * PRESENTATION save from a content one — the editor in particular must not
   * reseed its text for one, since nothing an author typed has changed and a
   * reseed would replace what is under their caret.
   */
  | "layout"
  | "unloaded";

export interface WorkflowChangedEvent {
  readonly projectId: string;
  readonly document: WorkflowDocument | null;
  readonly origin: WorkflowChangeOrigin;
}

export interface IProjectWorkflowModel {
  /**
   * Rewrites `workflow_ui` alone, leaving every authored field untouched.
   *
   * A narrow path rather than a `saveDocumentAsync` call at the call site,
   * because it has to flush the editor's debounced keystrokes FIRST: a layout
   * write that captured the document, awaited, and saved while the editor's
   * save timer was in flight would persist the PRE-EDIT document and silently
   * revert the author's work. `flushPendingEditsAsync` is the existing answer
   * to exactly that race and is why the AI tools call it before every read.
   *
   * Pass null to clear the section — which is what a layout returning to its
   * designed arrangement produces.
   */
  saveWorkflowUiAsync(ui: WorkflowUiState | null): Promise<void>;

  /** Raised whenever the loaded workflow document changes (load, save, import, unload). */
  readonly onWorkflowChanged: Emitter<WorkflowChangedEvent>;

  /**
   * Raised when the cached validation report is recomputed — including when
   * the document did not change but the project catalog did (scene rename,
   * model delete, scene entities becoming resident). Does not reseed the
   * editor; the panel queue subscribes to this to refresh reference issues.
   */
  readonly onValidationChanged: Emitter<WorkflowValidationReport>;

  /** The currently loaded document for the active project, or null. */
  readonly loadedWorkflow: WorkflowDocument | null;

  /** True when the active project has a loaded workflow document. */
  readonly hasWorkflowForActiveProject: boolean;

  /**
   * The stored file exists but could not be parsed as a workflow document
   * (the parse errors, verbatim). The raw editor/fix queue and the Workflow
   * Builder's raw-file tool response are the repair surfaces. Explicit CREATE
   * still replaces these bytes; a SAVE from the empty WYSIWYG view still
   * refuses.
   */
  readonly loadError: string | null;

  /** Individual stored-file parse issues, including source locations when known. */
  readonly loadIssues: readonly WorkflowYamlIssue[];

  /** The raw stored text when the file is unreadable ({@link loadError}); "" otherwise. */
  readonly unreadableRawText: string;

  /** Loads (or returns the cached) workflow for the active project; null when none exists. */
  getWorkflowAsync(signal?: AbortSignal): Promise<WorkflowDocument | null>;

  /**
   * Imports raw workflow YAML (e.g. from an uploaded `.yaml` file). Always
   * mints a new `uid` and binds `project` to the active project so the imported
   * workflow cannot alias its source's summary or per-user state.
   *
   * A file that does not parse is still imported: it becomes the unreadable
   * stored state ({@link loadError} + {@link unreadableRawText}) so the panel
   * can show the bytes and the issues. Returns `null` in that case.
   */
  importAsync(rawText: string, signal?: AbortSignal): Promise<WorkflowDocument | null>;

  /**
   * Creates this project's workflow from the starter template. Idempotent: it
   * performs the real cloud load first and returns any existing document, so a
   * call racing the initial load adopts the stored file rather than replacing
   * it, and a transient load failure THROWS before anything is written.
   *
   * Explicitly invoked only — opening the Workflow editor does NOT call this
   * (user direction). Seeding on open meant every project anyone glanced at
   * ended up carrying an empty template, with nothing to distinguish
   * "authored here" from "the panel was opened once".
   */
  createFromTemplateAsync(signal?: AbortSignal): Promise<WorkflowDocument>;

  /**
   * Deletes this project's workflow outright — the destructive sibling of
   * {@link createFromTemplateAsync}. Removes the stored `workflow.yaml`, drops
   * its searchable DynamoDB row, and leaves the project with NO workflow (the
   * loaded document becomes null and `onWorkflowChanged` fires with
   * `document: null`, which stops any run and clears this user's progress).
   *
   * Deliberately a delete rather than a reset-to-template: since a workflow is
   * only created on request, "no workflow" is a real state a project can be
   * returned to, and an empty template left behind would keep claiming the
   * project has one.
   *
   * Per-user progress rows are NOT swept: a client can only address its own,
   * and a workflow created later is minted with a fresh `uid`, so any rows left
   * behind are orphaned rather than adopted.
   *
   * Safe when the project has no workflow (the object delete tolerates a
   * missing key).
   */
  deleteWorkflowAsync(signal?: AbortSignal): Promise<void>;

  /**
   * Saves a document snapshot as the new loaded workflow. The document is
   * re-stamped with the active project's identity, serialized canonically,
   * debounce-uploaded to S3, projected to DynamoDB when summary fields change,
   * and emitted via `onWorkflowChanged`.
   */
  saveDocumentAsync(
    document: WorkflowDocument,
    origin: WorkflowChangeOrigin,
    signal?: AbortSignal,
  ): Promise<WorkflowDocument>;

  /**
   * Full-file save from raw YAML text (the dev raw editor, and
   * update_workflow's unreadable-file repair mode). Throws with the parse
   * errors when the text is not a valid workflow document — the caller surfaces
   * them instead of a broken parse ever becoming the loaded state.
   * Identity (uid / project) is re-stamped so a hand-edited file cannot lose
   * its binding.
   */
  saveYamlTextAsync(
    rawYamlText: string,
    origin: WorkflowChangeOrigin,
    signal?: AbortSignal,
  ): Promise<WorkflowDocument>;

  /**
   * Applies an edited authored-view text (the WYSIWYG projection) back onto
   * the document: the authored layer is replaced, machine data (step ids,
   * prepared scripts and identity) is carried over by step matching.
   */
  saveAuthoredViewAsync(
    editedText: string,
    origin: WorkflowChangeOrigin,
    signal?: AbortSignal,
  ): Promise<WorkflowDocument>;

  /**
   * Reads the active user's durable state for the loaded workflow from
   * DynamoDB. Returns null when no row has been written yet.
   */
  getWorkflowStateAsync(signal?: AbortSignal): Promise<WorkflowRunState | null>;

  /**
   * Replaces the active user's durable state for the loaded workflow in
   * DynamoDB. This never mutates or uploads `workflow.yaml`.
   */
  updateWorkflowStateAsync(
    state: WorkflowRunState,
    signal?: AbortSignal,
  ): Promise<void>;

  /**
   * Applies a sparse YAML patch to the workflow — the `update_workflow` edit
   * path. Keys that are present are applied, keys that are absent leave their
   * field untouched, and an explicit `null` clears one.
   *
   * When the project has no workflow file yet, the document is created from
   * the patch and stamped with the same identity a template-seeded file gets.
   *
   * Throws when the YAML is malformed, references an unknown key, or tries to
   * write identity keys or the retired `workflow_state`.
   */
  applyYamlAsync(
    yamlText: string,
    origin: WorkflowChangeOrigin,
    signal?: AbortSignal,
  ): Promise<WorkflowDocument>;

  /** The loaded workflow serialized to its canonical YAML text ("" when none). */
  getWorkflowYamlText(): string;

  /** Validates the loaded document (structural errors + live project references). */
  validateLoadedWorkflow(): WorkflowValidationReport;

  /**
   * Same checks as {@link validateLoadedWorkflow} against an in-memory
   * document (the raw editor's current parse). No cache write — the panel uses
   * this so a typed `scene: 7` shows before save.
   */
  checkReferences(document: WorkflowDocument): WorkflowValidationReport;

  /**
   * Last report from {@link validateLoadedWorkflow} / a catalog re-check.
   * Empty-document errors when nothing is loaded. The panel queue reads this
   * after {@link onValidationChanged} rather than recomputing on every tick.
   */
  readonly lastValidationReport: WorkflowValidationReport;

  /** Cancels the debounce and uploads any pending edit immediately. */
  flushPendingUploadAsync(): Promise<void>;

  /**
   * Registers the EDITOR's pending-keystroke flusher (the registered-host
   * pattern — the model owns *when* pending edits must land, the viewmodel
   * owns the debounced text). The Workflow panel registers its debounced-save
   * flush here; `null` unregisters. One registrant at a time.
   */
  setPendingEditFlusher(flusher: (() => Promise<void>) | null): void;

  /**
   * Writes any debounced editor keystrokes into the model NOW (no-op when
   * nothing is pending or no flusher is registered). Agent read/write paths
   * (`get_workflow` / `update_workflow` / `play_workflow_script`) await this
   * first so the builder always reads the text the user actually sees —
   * without it, a tool call landing inside the editor's save debounce reads a
   * pre-keystroke snapshot, and the stale debounced save then stomps the
   * agent's edit.
   */
  flushPendingEditsAsync(): Promise<void>;

  dispose(): void;
}

export const PROJECT_WORKFLOW_MODEL = Symbol.for("Campfire.ProjectWorkflowModel");
